import { NOTIFICATION_DIALOG_ACTIONS } from '@src/app/notifications/dialog';
import { useNotifilcationDialog } from '@src/app/notifications/dialog/hooks';
import { useFormContext } from '@src/shared/form';
import { formatDateRange, getServerErrorMessage } from '@src/shared/lib';
import { Button, SubmitButton } from '@src/shared/ui/Button';
import { Form } from '@src/shared/ui/form';
import { Stack } from '@src/shared/ui/layout';
import Typography from '@src/shared/ui/Typography';
import { createSchema, Yup } from '@src/shared/validation';
import { useEffect, useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTimeline } from '../../model/hooks';
import { useAbsenceTypes } from '../../model/query';
import type { Absence, AbsenceType } from '../../types';
import {
  ABSENCE_LOCK_STATUS,
  isAbsenceLockErrorStatus,
  isAbsenceLockHeldStatus,
  type SlotData,
} from '../../types';
import FormFieldset from './Fields';
import FormHeader from './FormHeader';

type OverrideAbsenceDetail = {
  key: string;
  typeName: string;
  from: Date;
  to: Date;
  startTime: string | null;
  endTime: string | null;
};

function getOverrideAbsenceDetails(
  selectedIds: number[],
  absences: Absence[],
  absenceTypes: AbsenceType[],
): OverrideAbsenceDetail[] {
  const selectedAbsences = absences.filter((absence) =>
    selectedIds.includes(absence.id),
  );

  const groupedAbsences = new Map<string, OverrideAbsenceDetail>();
  const hourlyAbsences: OverrideAbsenceDetail[] = [];

  selectedAbsences.forEach((absence) => {
    const typeName =
      absence.typeId != null
        ? absenceTypes.find((type) => type.id === absence.typeId)?.name
        : undefined;
    const resolvedTypeName = typeName ?? 'Unknown';
    const isHourly = !!absence.startTime && !!absence.endTime;

    if (isHourly) {
      hourlyAbsences.push({
        key: `absence-${absence.id}`,
        typeName: resolvedTypeName,
        from: absence.date,
        to: absence.date,
        startTime: absence.startTime,
        endTime: absence.endTime,
      });
      return;
    }

    const groupKey =
      absence.typeId != null
        ? `type-${absence.typeId}`
        : `absence-${absence.id}`;
    const existingGroup = groupedAbsences.get(groupKey);

    if (!existingGroup) {
      groupedAbsences.set(groupKey, {
        key: groupKey,
        typeName: resolvedTypeName,
        from: absence.date,
        to: absence.date,
        startTime: null,
        endTime: null,
      });
      return;
    }

    if (absence.date < existingGroup.from) {
      existingGroup.from = absence.date;
    }

    if (absence.date > existingGroup.to) {
      existingGroup.to = absence.date;
    }
  });

  return [...groupedAbsences.values(), ...hourlyAbsences].sort(
    (a, b) => a.from.getTime() - b.from.getTime(),
  );
}

export type ScheduleFormValues = {
  userId: number | null;
  dateFrom: Date | null;
  dateTo?: Date | null;
  startTime?: string | null;
  endTime?: string | null;
  typeId: number | null;
  tentative?: boolean;
  notes?: string;
};

function getDefaultValues(slotData: SlotData | null): ScheduleFormValues {
  const values: ScheduleFormValues = {
    userId: null,
    dateFrom: null,
    dateTo: null,
    startTime: null,
    endTime: null,
    typeId: null,
    tentative: false,
    notes: '',
  };
  if (!slotData) return values;

  const userId = slotData.userId;
  if (userId) values.userId = userId;

  const dateFrom = slotData.start;
  if (dateFrom) values.dateFrom = new Date(dateFrom);

  const dateTo = slotData.end;
  if (dateTo) values.dateTo = new Date(dateTo);

  values.startTime = slotData.startTime ?? null;
  values.endTime = slotData.endTime ?? null;
  values.typeId = slotData.typeId;
  values.tentative = slotData.tentative;
  values.notes = slotData.notes;

  return values;
}

function ScheduleFormActions({ disabled }: { disabled: boolean }) {
  const { absenceTypes } = useAbsenceTypes();
  const { setValue, watch } =
    useFormContext() as UseFormReturn<ScheduleFormValues>;
  const [typeId, tentative] = watch(['typeId', 'tentative']);
  const selectedType = absenceTypes.find((type) => type.id === typeId);
  const canSaveTentative = !!selectedType?.tentativeable;

  useEffect(() => {
    if (!canSaveTentative && tentative) {
      setValue('tentative', false);
    }
  }, [canSaveTentative, setValue, tentative]);

  return (
    <Stack spacing={1.25}>
      {canSaveTentative && (
        <Button
          disabled={disabled}
          variant="outlined"
          type="submit"
          onClick={() => setValue('tentative', true)}
        >
          <Typography variant="body1">Save as Tentative</Typography>
        </Button>
      )}
      <SubmitButton
        disabled={disabled}
        variant="contained"
        loadingPosition="start"
        onClick={() => setValue('tentative', false)}
      >
        <Typography variant="body1">Request</Typography>
      </SubmitButton>
    </Stack>
  );
}

export default function ScheduleForm() {
  const {
    absenceLock,
    selectedSlot,
    selectedSlotData,
    book,
    isDayView,
    absences,
  } = useTimeline();
  const { absenceTypes } = useAbsenceTypes();

  const schema = useMemo(
    () =>
      createSchema({
        userId: Yup.number().required('This field is required'),
        dateFrom: Yup.date().required('This field is required'),
        dateTo: isDayView
          ? Yup.date().nullable()
          : Yup.date().required('This field is required'),
        typeId: Yup.number().required('This field is required'),
        ...(isDayView
          ? {
              startTime: Yup.string().required('This field is required'),
              endTime: Yup.string().required('This field is required'),
            }
          : {}),
      }),
    [isDayView],
  );
  const alert = useNotifilcationDialog();
  const [serverError, setServerError] = useState<string | null>(null);

  const lockMessage = useMemo(() => {
    if (isAbsenceLockErrorStatus(absenceLock.status)) {
      return (
        absenceLock.message || 'This user is currently locked for editing.'
      );
    }
    return null;
  }, [absenceLock.message, absenceLock.status]);

  const isFormEditable = isAbsenceLockHeldStatus(absenceLock.status);

  useEffect(() => {
    if (isAbsenceLockHeldStatus(absenceLock.status) || !selectedSlot?.userId) {
      setServerError(null);
    }
  }, [absenceLock.status, selectedSlot?.userId]);

  const onSubmit = async (values: Record<string, unknown>) => {
    setServerError(null);
    const payload = {
      ...values,
      absenceId: selectedSlot?.editedAbsenceId ?? null,
      userId: selectedSlot?.userId,
      dateFrom: values.dateFrom,
      dateTo: !values.dateTo ? values.dateFrom : values.dateTo,
    };

    const editedAbsenceIds =
      selectedSlot?.editedAbsenceIds ??
      (selectedSlot?.editedAbsenceId
        ? [selectedSlot.editedAbsenceId]
        : []);
    const editedAbsence =
      editedAbsenceIds.length > 0
        ? absences.find((a) => editedAbsenceIds.includes(a.id))
        : null;
    const isTypeChanged =
      !!editedAbsence && editedAbsence.typeId !== values.typeId;

    if (!selectedSlot?.selectedIds.length && !isTypeChanged) {
      return book(payload);
    }

    if (isDayView) {
      setServerError(
        'Selected time overlaps an existing hourly absence for this user.',
      );
      return undefined;
    }

    const overrideIds =
      isTypeChanged && editedAbsenceIds.length > 0
        ? [
            ...new Set([
              ...(selectedSlot?.selectedIds ?? []),
              ...editedAbsenceIds,
            ]),
          ]
        : selectedSlot?.selectedIds ?? [];

    const start = selectedSlot?.start;
    const end = selectedSlot?.end;
    if (start && end) {
      const overrideDetails = getOverrideAbsenceDetails(
        overrideIds,
        absences,
        absenceTypes,
      );
      const { action } = await alert.open({
        title: 'Warning',
        children: (
          <Stack spacing={1} alignItems="center">
            <Typography variant="body1">
              You are about to overide existing absence events:
            </Typography>
            <table>
              <tbody>
                {overrideDetails.map(
                  ({ key, typeName, from, to, startTime, endTime }) => (
                    <tr key={key}>
                      <td style={{ textAlign: 'left', paddingRight: 16 }}>
                        <Typography variant="h4">{typeName}:</Typography>
                      </td>
                      <td style={{ textAlign: 'left' }}>
                        <Typography variant="h4">
                          {formatDateRange(from, to)}
                          {startTime && endTime
                            ? `, ${startTime} - ${endTime}`
                            : ''}
                        </Typography>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </Stack>
        ),
        slotProps: {
          confirmButton: { label: 'OK' },
          cancelButton: { label: 'Cancel' },
        },
      });
      if (action === NOTIFICATION_DIALOG_ACTIONS.CONFIRM) {
        return book(payload);
      }
      return undefined;
    }

    return book(payload);
  };

  return (
    <Form
      schema={schema}
      defaultValues={getDefaultValues(selectedSlotData)}
      onError={(error, info) => {
        if (!info.isInvalid) {
          setServerError(getServerErrorMessage(error));
        }
      }}
      onSubmit={(values) => onSubmit(values)}
    >
      {(lockMessage || serverError) && (
        <FormHeader
          tone={lockMessage ? 'warning' : 'error'}
          message={(lockMessage || serverError) as string}
        />
      )}
      <FormFieldset
        disabled={!isFormEditable}
        disableUserField={absenceLock.status === ABSENCE_LOCK_STATUS.ACQUIRING}
      />
      <ScheduleFormActions disabled={!isFormEditable} />
    </Form>
  );
}
